import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  {
    title: "Informations",
    links: [
      { label: "Accueil", to: "/" },
      { label: "Devis gratuit", to: "/demande-de-devis" },
      { label: "Tarifs déménagement", to: "/tarifs-demenagement-paris" },
      { label: "FAQ", to: "/faq" },
      { label: "Avis clients", to: "/#avis" },
      { label: "Galerie", to: "/#galerie" },
    ],
  },
  {
    title: "Légal",
    links: [
      { label: "Mentions légales", to: "/mentions-legales" },
      { label: "Politique de confidentialité", to: "/politique-de-confidentialite" },
      { label: "Conditions générales", to: "/conditions-generales-dutilisation" },
      { label: "Utilisation des cookies", to: "/utilisation-des-cookies" },
    ],
  },
];

export default function Footer() {
  const [stats, setStats] = useState({ rating: 4.9, count: 704 });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reviews`)
      .then((res) => {
        if (cancelled) return;
        const r = res.data?.rating;
        const c = res.data?.userRatingCount;
        if (r && c) setStats({ rating: r, count: c });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer data-testid="site-footer" className="bg-[#237cb5] text-white">
      <div className="bd-container py-16 sm:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Link to="/" aria-label="Big Déménagement - Accueil" className="inline-block">
            <img
              src="/images/logo-blanc.webp"
              alt="Logo Big Déménagement — déménageur à Paris et en Île-de-France"
              width="260"
              height="92"
              className="h-14 sm:h-16 w-auto"
              loading="lazy"
              decoding="async"
            />
          </Link>
          <p className="mt-3 text-[11px] tracking-[0.22em] uppercase text-white/55">
            Paris · Île-de-France
          </p>
          <Link
            to="/presentation"
            data-testid="footer-link-presentation"
            className="mt-3 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-white/85 hover:text-white border-b border-white/30 hover:border-white pb-0.5 transition-colors"
          >
            Présentation
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <p className="mt-5 text-sm text-white/70 leading-relaxed">
            Société de déménagement basée à Paris, intervenant à Paris et en Île-de-France pour les particuliers
            et les entreprises.
          </p>

          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-white/90 mt-0.5 shrink-0" />
              <span className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                <a href="tel:+33183885290" data-testid="footer-tel-1" className="text-white/85 hover:text-white">
                  01 83 88 52 90
                </a>
                <span className="text-white/40">·</span>
                <a href="tel:+33185117279" data-testid="footer-tel-2" className="text-white/85 hover:text-white">
                  01 85 11 72 79
                </a>
                <span className="text-white/40">·</span>
                <Link
                  to="/contact"
                  data-testid="footer-link-contact"
                  className="text-white/85 hover:text-white font-semibold underline underline-offset-2 decoration-white/40 hover:decoration-white"
                >
                  Contact
                </Link>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-white/90 mt-0.5 shrink-0" />
              <a
                href="mailto:contact@bigdem.fr"
                data-testid="footer-email"
                className="text-white/85 hover:text-white"
              >
                contact@bigdem.fr
              </a>
            </li>
            <li className="flex items-center gap-3 text-white/85" data-testid="footer-rating">
              <span className="flex" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4" fill="#FBBC05" stroke="#FBBC05" />
                ))}
              </span>
              <span className="font-semibold">{stats.rating.toFixed(1).replace(".", ",")}/5</span>
              <span className="text-white/65">· {stats.count.toLocaleString("fr-FR")} avis Google</span>
            </li>
          </ul>
        </div>

        {COLS.map((c) => (
          <div key={c.title} className="lg:col-span-2">
            <h3 className="text-sm font-bold tracking-wider uppercase text-white/90">{c.title}</h3>
            <ul className="mt-5 space-y-2.5 text-sm">
              {c.links.map((l) => (
                <li key={l.label}>
                  {l.to.startsWith("/") || l.to === "#" ? (
                    <Link
                      to={l.to === "#" ? "#" : l.to}
                      className="text-white/70 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a href={l.to} className="text-white/70 hover:text-white transition-colors">
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="lg:col-span-4">
          <h3 className="text-sm font-bold tracking-wider uppercase text-white/90">Devis gratuit</h3>
          <p className="mt-5 text-sm text-white/70">
            Recevez une estimation adaptée à votre projet, sans engagement.
          </p>
          <Link
            to="/demande-de-devis"
            data-testid="footer-cta-devis"
            className="mt-5 h-12 inline-flex items-center justify-center gap-2 rounded-xl px-6 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
          >
            Devis gratuit
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="bd-container py-6 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-xs text-white/60">
          <p>© {new Date().getFullYear()} Big Déménagement — Société de déménagement à Paris et en Île-de-France</p>
          <p>SIRET 892 126 665 00025 · Paris 9e</p>
        </div>
      </div>
    </footer>
  );
}
